// src/hooks/seo/useSeoSettings.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

/** Adds x-admin-ok header when the EdibleArtControl local flag is present */
function adminHeaders(): HeadersInit {
  try {
    const ok = typeof window !== 'undefined' && localStorage.getItem('fca_admin_authed_v1') === '1';
    return ok ? { 'x-admin-ok': '1' } : {};
  } catch {
    return {};
  }
}

/** Fetch the full SEO bundle (settings, pages, images). PIN is optional and omitted when empty. */
export function useSeoBundle(pin?: string) {
  return useQuery({
    queryKey: ['seo-bundle'],
    queryFn: async () => {
      const url = pin
        ? '/api/seo?action=getBundle&pin=' + encodeURIComponent(pin)
        : '/api/seo?action=getBundle';
      const r = await fetch(url, {
        credentials: 'include',
        headers: adminHeaders(),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'failed');
      return j as { ok: true; settings: any; pages: any[]; images: any[] };
    },
  });
}

/** Save site/page/image records through the single API */
export function useSaveSeo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const action =
        payload?.settings ? 'saveSettings' :
        payload?.page     ? 'savePage'     :
        payload?.image    ? 'saveImage'    : 'unknown';

      const body: any = { action, ...payload };
      if (payload?.pin) body.pin = payload.pin; // only include if provided

      const r = await fetch('/api/seo', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...adminHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || j.message || 'Failed to save data.');
      return j;
    },    onSuccess: () => {
      // refresh the bundle after any save
      return qc.invalidateQueries({ queryKey: ['seo-bundle'] });
    },
  });
}

/**
 * Ask the API to generate AI suggestions.
 * - useAISuggest → returns just the suggestion string (backward compatible with your UI)
 * - useAISuggestRich → returns the full object if/when you want the extra metadata
 */
export function useAISuggest() {
  return async function ai(fieldKey: string, context: any, pin?: string) {
    const body: any = { action: 'aiSuggest', fieldKey, context };
    if (pin) body.pin = pin;

    const r = await fetch('/api/seo', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify(body),
    });
    const j = await r.json();
    if (!j.ok) throw new Error(j.error || 'failed');

    // Return ONLY the suggestion so existing components don’t crash
    return j.suggestion as string;
  };
}

export function useAISuggestRich() {
  return async function ai(fieldKey: string, context: any, pin?: string) {
    const body: any = { action: 'aiSuggest', fieldKey, context };
    if (pin) body.pin = pin;

    const r = await fetch('/api/seo', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify(body),
    });
    const j = await r.json();
    if (!j.ok) throw new Error(j.error || 'failed');

    return j as {
      ok: true;
      suggestion: string;
      bestProvider: 'openai' | 'gemini' | 'cache' | 'fallback';
      models: { openai: string | null; gemini: string | null };
      note?: string;
    };
  };
}

export function useAISuggestBatch() {
  return async function batch(items: Array<{ fieldKey: string; context: any; pin?: string }>) {
    const payload = {
      action: 'aiSuggest.batch',
      items: items.map(it => {
        const { fieldKey, context, pin } = it;
        const body: any = { fieldKey, context };
        if (pin) body.pin = pin;
        return body;
      })
    };
    const r = await fetch('/api/seo', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify(payload)
    });
    const j = await r.json();
    if (!j.ok) throw new Error(j.error || 'failed');
    // returns: { ok, results: [{ fieldKey, suggestion, bestProvider, cached }] }
    return j.results as Array<{ fieldKey: string; suggestion: string; bestProvider: string; cached: boolean }>;
  };
}
